import React, { useEffect, useRef, useState } from "react";

import { makeGet } from "@culturehq/client";
import { classnames, EmailField } from "@culturehq/components";

import { checkOrgAllowedDomain, t, Trans } from "app/utils";
import checkEmail from "app/utils/checkEmail";
import { PostStoryState, UserInformationState } from "../../types";

interface EmailValidationProps {
  selectedPrompt: PostStoryState["selectedPrompt"];
  onUserAccessStatusFetched: (newState: Partial<UserInformationState>) => void;
}
const EmailValidation: React.FC<EmailValidationProps> = ({
  selectedPrompt,
  onUserAccessStatusFetched
}) => {
  const isMounted = useRef<boolean>();
  const [email, setEmail] = useState<string>("");
  const [emailValidated, setEmailValidated] = useState(false);
  const isValidEmail = !checkEmail(email);
  const [error, setError] = useState<string | undefined>();

  const getUserAccessStatus = () => makeGet(`/stories/public/validation/${selectedPrompt?.id}`, { email })
    .then((response: any) => {
      let allowUser = true;
      if (isMounted.current) {
        allowUser = checkOrgAllowedDomain(response.organization.name, email);
        if (!allowUser) {
          setError(t("Sorry this email is not accepted. Please try another."));
        } else {
          setError(undefined);
        }

        if (allowUser) {
          onUserAccessStatusFetched({
            email,
            error: undefined,
            userAccessStatus: response.userAccessStatus,
            creator: response.creator,
            organization: response.organization
          });
        }
      }
    })
    .catch(error => {
      onUserAccessStatusFetched({ error });
    });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onUserAccessStatusFetched({
      email: undefined,
      userAccessStatus: undefined,
      creator: undefined,
      organization: { privacyPolicies: [] }
    });
    setEmailValidated(false);
    if (e.key !== "Enter") return;

    if (isValidEmail) {
      getUserAccessStatus();
      setEmailValidated(true);
    }
  };

  const handleOnBlur = () => {
    if (isValidEmail) {
      getUserAccessStatus();
      setEmailValidated(true);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className="Email" onBlur={handleOnBlur}>
      <EmailField
        name="email"
        className={classnames(
          "EmailField",
          { "EmailField--Valid": isValidEmail },
          { "EmailField--Validated": emailValidated }
        )}
        required
        suffixIcon={emailValidated ? "checkmark-filled" : "arrow-left-c"}
        suffixText={!emailValidated ? "Enter" : undefined}
        value={email}
        onChange={setEmail}
        onKeyDown={handleKeyDown}
        validator={checkEmail}
        validEmailAnimation
        validEmail={emailValidated}
      >
        <Trans>Enter your email*</Trans>
      </EmailField>
      {error && <span className="DomainCheck">{error}</span>}
    </div>
  );
};

export default EmailValidation;
